import { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import {
  Building2,
  Camera,
  CheckCircle2,
  ChevronRight,
  Lock,
  Mail,
  MapPin,
  MessageCircle,
  Pencil,
  Phone,
  Save,
  Share2,
  User,
  X,
} from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import Button from "../../components/ui/Button";
import { fetchProviderProfile, updateProviderProfile } from "../../services/profileApi";
import { updateProfile } from "../../store/slices/authSlice";
import { readImageAsDataUrl } from "../../utils/readImageAsDataUrl";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";
import { showError, showSuccess } from "../../utils/toast";

const SECTIONS = [
  {
    id: "profile",
    label: "Profile",
    description: "Your login email and phone",
    icon: User,
  },
  {
    id: "institution",
    label: "Institution",
    description: "Organisation profile on SimCollect",
    icon: Building2,
  },
];

const inputClass =
  "w-full rounded-lg border border-primary/10 bg-white px-3 py-2.5 text-sm text-primary shadow-sm transition-colors focus:border-primary/25 focus:outline-none focus:ring-2 focus:ring-primary/10 disabled:cursor-not-allowed disabled:bg-primary/[0.03] disabled:text-primary/70";

function profileFormFromUser(user) {
  return {
    legal_name: user?.legal_name || user?.name || "",
    primary_email: user?.primary_email || user?.email || "",
    primary_phone_number: user?.primary_phone_number || user?.phone_number || user?.phone || "",
  };
}

function providerFormFromUser(user) {
  const socials = user?.socials && typeof user.socials === "object" ? user.socials : {};
  return {
    legal_name: user?.legal_name || user?.institution || user?.name || "",
    business_type: user?.business_type || user?.type || "",
    region: user?.region || "",
    city: user?.city || "",
    gps_address: user?.gps_address || "",
    primary_email: user?.primary_email || user?.email || "",
    primary_phone_number: user?.primary_phone_number || user?.phone_number || user?.phone || "",
    secondary_email: user?.secondary_email || "",
    secondary_phone_number: user?.secondary_phone_number || "",
    website: user?.website || "",
    logo: resolveMediaUrl(user?.logo) || null,
    socials: {
      instagram: socials.instagram || "",
      x: socials.x || socials.twitter || "",
      facebook: socials.facebook || "",
      tiktok: socials.tiktok || "",
      youtube: socials.youtube || "",
    },
    channels: {
      sms: Boolean(user?.channels?.sms),
      whatsapp: Boolean(user?.channels?.whatsapp),
      email: Boolean(user?.channels?.email ?? true),
    },
  };
}

export default function SettingsPage() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.auth);
  const logoInputRef = useRef(null);

  const [active, setActive] = useState("profile");
  const [profileEditing, setProfileEditing] = useState(false);
  const [providerEditing, setProviderEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState(() => profileFormFromUser(user));
  const [providerForm, setProviderForm] = useState(() => providerFormFromUser(user));
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    if (location.state?.section && SECTIONS.some((s) => s.id === location.state.section)) {
      setActive(location.state.section);
    }
  }, [location.state?.section]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setProfileLoading(true);
    fetchProviderProfile(token)
      .then((fresh) => {
        if (!cancelled && fresh) dispatch(updateProfile(fresh));
      })
      .catch(() => {
        /* keep persisted user */
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, dispatch]);

  useEffect(() => {
    if (!profileEditing) setProfileForm(profileFormFromUser(user));
    if (!providerEditing) setProviderForm(providerFormFromUser(user));
  }, [user, profileEditing, providerEditing]);

  const activeSection = SECTIONS.find((s) => s.id === active) ?? SECTIONS[0];
  const profileBaseline = useMemo(() => profileFormFromUser(user), [user]);
  const providerBaseline = useMemo(() => providerFormFromUser(user), [user]);

  const profileDirty = JSON.stringify(profileForm) !== JSON.stringify(profileBaseline);
  const providerDirty = JSON.stringify(providerForm) !== JSON.stringify(providerBaseline);

  const profileCanSubmit =
    profileForm.legal_name.trim() &&
    profileForm.primary_email.trim().includes("@") &&
    profileForm.primary_phone_number.trim();

  const handleProfileSave = async () => {
    if (!profileEditing || !profileCanSubmit || !profileDirty || saving) return;
    setSaving(true);
    try {
      const updated = await updateProviderProfile(token, profileForm, profileBaseline);
      dispatch(updateProfile(updated));
      setProfileEditing(false);
      showSuccess("Profile updated.");
    } catch (err) {
      showError(err.message || "Could not update profile.");
    } finally {
      setSaving(false);
    }
  };

  const handleProviderSave = async () => {
    if (!providerEditing || !providerDirty || saving) return;
    setSaving(true);
    try {
      const updated = await updateProviderProfile(token, providerForm, providerBaseline);
      dispatch(updateProfile(updated));
      setProviderEditing(false);
      showSuccess("Provider details updated.");
    } catch (err) {
      showError(err.message || "Could not update provider details.");
    } finally {
      setSaving(false);
    }
  };

  const updateProviderField = (key, value) => {
    if (!providerEditing) return;
    setProviderForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateSocialField = (key, value) => {
    if (!providerEditing) return;
    setProviderForm((prev) => ({
      ...prev,
      socials: { ...prev.socials, [key]: value },
    }));
  };

  const updateChannelField = (key, checked) => {
    if (!providerEditing) return;
    setProviderForm((prev) => ({
      ...prev,
      channels: { ...prev.channels, [key]: checked },
    }));
  };

  const handleLogoPick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !providerEditing) return;
    try {
      const dataUrl = await readImageAsDataUrl(file);
      updateProviderField("logo", dataUrl);
    } catch (err) {
      showError(err.message || "Could not read logo.");
    }
  };

  const displayName = providerForm.legal_name || profileForm.legal_name || "Your provider";
  const logoSrc = resolveMediaUrl(providerForm.logo || user?.logo);

  return (
    <PageShell
      title="Settings"
      description="Manage your institution profile and portal branding."
      actions={
        active === "profile" ? (
          profileEditing ? (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setProfileForm(profileBaseline);
                  setProfileEditing(false);
                }}
                disabled={saving}
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleProfileSave}
                disabled={!profileDirty || !profileCanSubmit}
                loading={saving}
              >
                <Save className="h-4 w-4" />
                Save changes
              </Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => setProfileEditing(true)}>
              <Pencil className="h-4 w-4" />
              Edit profile
            </Button>
          )
        ) : active === "institution" ? (
          providerEditing ? (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setProviderForm(providerBaseline);
                  setProviderEditing(false);
                }}
                disabled={saving}
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleProviderSave} disabled={!providerDirty} loading={saving}>
                <Save className="h-4 w-4" />
                Save changes
              </Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => setProviderEditing(true)}>
              <Pencil className="h-4 w-4" />
              Edit provider
            </Button>
          )
        ) : null
      }
    >
      <div className="mb-5 overflow-hidden rounded-xl border border-primary/[0.07] bg-gradient-to-br from-primary via-[#1a5244] to-[#7F1D1D] p-5 text-white shadow-[0_4px_20px_rgba(127,29,29,0.25)] sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white/15 text-lg font-bold ring-1 ring-white/20 backdrop-blur-sm">
              {logoSrc ? (
                <img src={logoSrc} alt="" className="h-full w-full object-contain" />
              ) : (
                displayName
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((w) => w[0])
                  .join("")
                  .toUpperCase()
              )}
            </span>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/50">
                Provider portal
              </p>
              <h2 className="mt-0.5 text-lg font-bold sm:text-xl">{displayName}</h2>
              <p className="mt-1 text-xs text-white/60">{profileForm.primary_email}</p>
            </div>
          </div>
          <span className="rounded-lg bg-white/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ring-1 ring-white/15">
            Institution account
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
        <nav className="flex shrink-0 gap-2 overflow-x-auto pb-1 lg:w-60 lg:flex-col lg:overflow-visible lg:pb-0">
          {SECTIONS.map((section) => {
            const isActive = active === section.id;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => setActive(section.id)}
                className={`group relative flex min-w-[200px] items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-all lg:min-w-0 ${
                  isActive
                    ? "border-primary/15 bg-white shadow-[0_2px_8px_rgba(127,29,29,0.08)] ring-1 ring-primary/10"
                    : "border-transparent bg-primary/[0.02] hover:border-primary/10 hover:bg-white"
                }`}
              >
                {isActive && (
                  <span className="absolute inset-y-2 left-0 w-[3px] rounded-full bg-primary" aria-hidden />
                )}
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isActive ? "bg-primary text-white" : "bg-primary/[0.06] text-primary/50"
                  }`}
                >
                  <section.icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-bold ${isActive ? "text-primary" : "text-primary/70"}`}>
                    {section.label}
                  </p>
                  <p className="mt-0.5 text-[11px] text-primary/40">{section.description}</p>
                </div>
                <ChevronRight
                  className={`mt-1 h-4 w-4 shrink-0 ${isActive ? "text-primary/40" : "text-primary/20"}`}
                />
              </button>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1 space-y-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/[0.06] text-primary">
              <activeSection.icon className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-base font-bold text-primary">{activeSection.label}</h2>
              <p className="text-xs text-primary/40">{activeSection.description}</p>
            </div>
          </div>

          {active === "profile" && (
            <SettingsPanel
              title="Your profile"
              subtitle="Fields match the providers table: legal name, primary email, and primary phone."
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Legal name" icon={User} className="sm:col-span-2">
                  <input
                    type="text"
                    className={inputClass}
                    value={profileForm.legal_name}
                    onChange={(e) =>
                      profileEditing && setProfileForm((p) => ({ ...p, legal_name: e.target.value }))
                    }
                    disabled={!profileEditing}
                    autoComplete="organization"
                  />
                </Field>
                <Field label="Primary email" icon={Mail}>
                  <input
                    type="email"
                    className={inputClass}
                    value={profileForm.primary_email}
                    onChange={(e) =>
                      profileEditing &&
                      setProfileForm((p) => ({ ...p, primary_email: e.target.value }))
                    }
                    disabled={!profileEditing}
                    autoComplete="email"
                  />
                </Field>
                <Field label="Primary phone" icon={Phone}>
                  <input
                    type="tel"
                    className={inputClass}
                    value={profileForm.primary_phone_number}
                    onChange={(e) =>
                      profileEditing &&
                      setProfileForm((p) => ({ ...p, primary_phone_number: e.target.value }))
                    }
                    disabled={!profileEditing}
                    autoComplete="tel"
                  />
                </Field>
              </div>
            </SettingsPanel>
          )}

          {active === "institution" && (
            <>
              <SettingsPanel
                title="Organisation"
                subtitle={
                  "Update institution branding and registration details."
                }
              >
                <div className="mb-4 flex flex-wrap items-center gap-4 rounded-xl border border-primary/10 bg-primary/[0.02] p-4">
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-primary/10">
                    {logoSrc ? (
                      <img src={logoSrc} alt="" className="h-full w-full object-contain" />
                    ) : (
                      <Building2 className="h-7 w-7 text-primary/30" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-primary/50">Institution logo</p>
                    <p className="mt-0.5 text-xs text-primary/40">PNG, JPG, or WebP up to 2 MB.</p>
                    {providerEditing ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => logoInputRef.current?.click()}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-primary/15 bg-white px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/[0.03]"
                        >
                          <Camera className="h-3.5 w-3.5" />
                          {providerForm.logo ? "Change logo" : "Upload logo"}
                        </button>
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="sr-only"
                          onChange={handleLogoPick}
                        />
                        {providerForm.logo ? (
                          <button
                            type="button"
                            onClick={() => updateProviderField("logo", null)}
                            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-primary/50 hover:bg-primary/[0.04] hover:text-primary"
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>

                {profileLoading ? (
                  <p className="mb-4 text-xs text-primary/45">Loading institution details…</p>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Institution code" icon={Lock} className="sm:col-span-2">
                    <div className="rounded-lg border border-primary/10 bg-primary/[0.03] px-3 py-2.5 text-sm font-medium text-primary/70">
                      {user?.code || "—"}
                    </div>
                    <p className="mt-1.5 text-[11px] text-primary/40">
                      Assigned at onboarding. Contact SimCollect operations to change.
                    </p>
                  </Field>
                  <Field label="Legal name" icon={Building2} className="sm:col-span-2">
                    <input
                      type="text"
                      className={inputClass}
                      value={providerForm.legal_name}
                      disabled={!providerEditing}
                      onChange={(e) => updateProviderField("legal_name", e.target.value)}
                    />
                  </Field>
                  <Field label="Business type">
                    <input
                      type="text"
                      className={inputClass}
                      value={providerForm.business_type}
                      disabled={!providerEditing}
                      onChange={(e) => updateProviderField("business_type", e.target.value)}
                    />
                  </Field>
                  <Field label="Region">
                    <input
                      type="text"
                      className={inputClass}
                      value={providerForm.region}
                      disabled={!providerEditing}
                      onChange={(e) => updateProviderField("region", e.target.value)}
                    />
                  </Field>
                  <Field label="City">
                    <input
                      type="text"
                      className={inputClass}
                      value={providerForm.city}
                      disabled={!providerEditing}
                      onChange={(e) => updateProviderField("city", e.target.value)}
                    />
                  </Field>
                  <Field label="GPS / physical address" icon={MapPin} className="sm:col-span-2">
                    <input
                      type="text"
                      className={inputClass}
                      value={providerForm.gps_address}
                      disabled={!providerEditing}
                      onChange={(e) => updateProviderField("gps_address", e.target.value)}
                    />
                  </Field>
                  <Field label="Website" className="sm:col-span-2">
                    <input
                      type="text"
                      className={inputClass}
                      value={providerForm.website}
                      disabled={!providerEditing}
                      onChange={(e) => updateProviderField("website", e.target.value)}
                    />
                  </Field>
                  <Field label="Secondary email" icon={Mail}>
                    <input
                      type="email"
                      className={inputClass}
                      value={providerForm.secondary_email}
                      disabled={!providerEditing}
                      onChange={(e) => updateProviderField("secondary_email", e.target.value)}
                    />
                  </Field>
                  <Field label="Secondary phone" icon={Phone}>
                    <input
                      type="tel"
                      className={inputClass}
                      value={providerForm.secondary_phone_number}
                      disabled={!providerEditing}
                      onChange={(e) => updateProviderField("secondary_phone_number", e.target.value)}
                    />
                  </Field>
                  <Field label="Agreed SLA (contract days)" icon={Lock} className="sm:col-span-2">
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/10 bg-primary/[0.03] px-3 py-2.5 text-sm text-primary/70">
                      <span className="font-medium tabular-nums text-primary">
                        {user?.agreed_sla_days != null ? `${user.agreed_sla_days} days` : "—"}
                      </span>
                      <Lock className="h-3.5 w-3.5 shrink-0 text-primary/30" />
                    </div>
                    <p className="mt-1.5 text-[11px] text-primary/40">
                      Set during onboarding with SimCollect. You cannot change contract SLA here;
                      operations manages manager and agent KPIs separately.
                    </p>
                  </Field>
                </div>
              </SettingsPanel>

              <SettingsPanel
                title="Social & web"
                subtitle="Public links shown on your institution profile (optional)."
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    { key: "instagram", label: "Instagram" },
                    { key: "facebook", label: "Facebook" },
                    { key: "x", label: "X (Twitter)" },
                    { key: "tiktok", label: "TikTok" },
                    { key: "youtube", label: "YouTube" },
                  ].map(({ key, label }) => (
                    <Field key={key} label={label} icon={Share2} className={key === "youtube" ? "sm:col-span-2" : ""}>
                      <input
                        type="url"
                        className={inputClass}
                        placeholder="https://"
                        value={providerForm.socials[key] || ""}
                        disabled={!providerEditing}
                        onChange={(e) => updateSocialField(key, e.target.value)}
                      />
                    </Field>
                  ))}
                </div>
              </SettingsPanel>

              <SettingsPanel
                title="Outreach channels"
                subtitle="Which channels SimCollect may use when contacting your customers on your behalf."
              >
                <div className="flex flex-wrap gap-3">
                  {[
                    { key: "sms", label: "SMS", icon: MessageCircle },
                    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
                    { key: "email", label: "Email", icon: Mail },
                  ].map(({ key, label, icon: Icon }) => (
                    <label
                      key={key}
                      className={`inline-flex cursor-pointer items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition-colors ${
                        providerForm.channels[key]
                          ? "border-primary/20 bg-primary/[0.06] text-primary"
                          : "border-primary/10 bg-white text-primary/50"
                      } ${!providerEditing ? "cursor-default opacity-90" : "hover:border-primary/15"}`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-primary/20 text-primary focus:ring-primary/20"
                        checked={Boolean(providerForm.channels[key])}
                        disabled={!providerEditing}
                        onChange={(e) => updateChannelField(key, e.target.checked)}
                      />
                      <Icon className="h-4 w-4 shrink-0 opacity-60" />
                      {label}
                    </label>
                  ))}
                </div>
                {!providerEditing && !providerForm.channels.sms && !providerForm.channels.whatsapp && !providerForm.channels.email ? (
                  <p className="mt-3 text-xs text-primary/40">No outreach channels enabled.</p>
                ) : null}
              </SettingsPanel>

              {providerEditing && providerDirty ? (
                <div className="flex justify-end">
                  <Button size="sm" onClick={handleProviderSave} loading={saving}>
                    <CheckCircle2 className="h-4 w-4" />
                    Save provider changes
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
}

function SettingsPanel({ title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-primary/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(127,29,29,0.04)] sm:p-5">
      <div className="mb-4 border-b border-primary/[0.06] pb-3">
        <h3 className="text-sm font-bold text-primary">{title}</h3>
        {subtitle ? <p className="mt-0.5 text-xs text-primary/40">{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

function Field({ label, icon: Icon, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-primary/50">
        {Icon && <Icon className="h-3.5 w-3.5 text-primary/30" />}
        {label}
      </label>
      {children}
    </div>
  );
}
